import { Layout, MenuProps, Skeleton, Typography } from 'antd';
import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import { css } from 'emotion';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { SWRConfig } from 'swr';
import { Dictionary } from 'ts-essentials';

import { WHITE } from 'config/colors';
import { selectPropertiesById } from 'state/properties/selectors';
import { getPureId } from 'state/routing/selectors';
import { PropertyType } from 'waypoint-types';
import BackToMap from './BackToMap';
import PropertyProfileMenu from './PropertyProfileMenu';
import { MenuItem } from './types';
import PropertyDetailSection from './PropertyProfileSection';
import { LeasesSection } from './LeasesSection';
import FinancialSection from './FinancialSection';
import {
    hasOccupancyTrendEnabled,
    hasBalanceSheetEnabled,
    hasCapitalTrackerEnabled,
    hasPlanningSectionEnabled,
    hasUnitMixEnabled,
    hasAgedReceivablesEnabled,
    hasOwnershipPercentageEnabled,
} from 'state/user/selectors';
import StrategyPlanningSection from './StrategyPlanningSection';
import { CapitalProjectsSection } from './CapitalProjectsSection';
import { useGetFilteredEntityCodes, useGetQueryParam } from 'waypoint-hooks';
import {
    setQueryParam,
    clearQueryParam,
} from 'waypoint-hooks/useGetQueryParam';
import { HOLD_SELL_CARD_TYPE } from 'components/holdSellAnalysis/HoldSellAnalysis';
import { SWOT_CARD_TYPE } from 'components/swotAnalysis/SWOTAnalysis';
import { MARKET_OVERVIEW_CARD_TYPE } from 'components/marketOverview/MarketOverview';
import { ANNUAL_PRIORITIES_AND_OBJECTIVES_CARD_TYPE } from 'components/annualPrioritiesAndObjectives/AnnualPrioritiesAndObjectives';
import { DisabledDashboard } from 'waypoint-react';
import { LEASING_GUIDELINES_CARD_TYPE } from 'components/leasingGuidelines/LeasingGuidelines';
import { CONTRACTS_CARD_TYPE } from 'components/contracts/ContractsContainer';
import { VALUATIONS_CARD_TYPE } from 'components/valuations/ValuationsTable';
import { ValuationsSection } from './ValuationsSection';
import { LEASING_PLAN_CARD_TYPE } from 'components/leasingPlan/LeasingPlanCard';
import { AgedReceivablesSection } from './AgedReceivablesSection';
import { AGED_RECEIVABLES_CARD_TYPE } from 'components/agedReceivables/AgedReceivablesCard';
import { EntityOwnershipDataLevelDropdown } from 'components/ownershipPercentage/EntityOwnershipDataLevelDropdown';
import { AsOfDateIcon } from 'components/app/as-of-date';
import theme from 'config/theme';
import { AppFeaturePermissions, usePermissions } from 'contexts';

const { Content, Header, Sider } = Layout;

interface PropertyDetailContainerProps {
    entityCode: string;
    propertyInfo: PropertyType;
    hasOccupancyTrendEnabled: boolean;
    hasBalanceSheetEnabled: boolean;
    hasCapitalTrackerEnabled: boolean;
    hasPlanningSectionEnabled: boolean;
    hasUnitMixEnabled: boolean;
    hasAgedReceivablesEnabled: boolean;
    hasOwnershipPercentageEnabled: boolean;
}

const mainContentPane = css`
    background-color: #fafafa;
    height: calc(100% - 50px);
    overflow-y: auto;
`;

const backToMap = css`
    align-items: center;
    position: fixed;
    top: 60px;
    left: 13px;
    display: flex;
    flex: auto;
    flex: 0 1;
    height: 32.5px;
    margin-bottom: 15px;
    justify-content: center;
    min-width: 120px;
`;

const propertyNameStyle = css`
    text-align: center;
    line-height: 0.5 !important;
    flex: auto;
`;

const dataLevelWrapper = css`
    display: flex;
    margin-right: 16px;
`;

const asOfDateContainer = css`
    display: flex;
    color: ${theme.colors.grays.dark};
    margin-top: 1px;
`;

const headerStyle = css`
    width: 100%;
    height: 50px;
    background-color: ${WHITE};
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    padding-right: 10px;
`;

const SECTION_KEY = 'section';
const SUBSECTION_KEY = 'subsection';

const filterShowItems = (menuItems: MenuItem[]): MenuItem[] =>
    menuItems.filter((item) => item.showItem === true);

const PropertyProfileContainer = ({
    entityCode,
    propertyInfo,
    hasOccupancyTrendEnabled,
    hasBalanceSheetEnabled,
    hasCapitalTrackerEnabled,
    hasPlanningSectionEnabled,
    hasUnitMixEnabled,
    hasAgedReceivablesEnabled,
    hasOwnershipPercentageEnabled,
}: PropertyDetailContainerProps) => {
    const { featurePermissions } = usePermissions();
    const [siderCollapsed, setSiderCollapsed] = useState(false);
    const sectionQueryParam = useGetQueryParam(SECTION_KEY);
    const subsectionQueryParam = useGetQueryParam(SUBSECTION_KEY);

    const entityCodes = useGetFilteredEntityCodes();

    const occupancy = {
        key: 'occupancy-trend',
        label: 'Occupancy Trend',
        showItem: featurePermissions?.includes(
            AppFeaturePermissions.OccupancyTrend
        ),
    };

    const unitMix = {
        key: 'leases-unit-mix',
        label: 'Unit Mix',
        showItem: featurePermissions?.includes(AppFeaturePermissions.UnitMix),
    };

    const leases: MenuItem[] = [
        {
            key: 'leases-expirations',
            label: 'Expiration Schedule',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.ExpirationSchedule
            ),
        },
        {
            key: 'leases-rent-roll',
            label: 'Rent Roll',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.RentRoll
            ),
        },
        {
            key: 'leases-top-tenants',
            label: 'Top Tenants',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.TopTenants
            ),
        },
    ];

    if (hasUnitMixEnabled) {
        leases.push(unitMix);
    }

    const leaseChildren = hasOccupancyTrendEnabled
        ? [occupancy, ...leases]
        : [...leases];

    const financials = [
        {
            key: 'financials-income-statement',
            label: 'Income Statement',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.IncomeStatement
            ),
        },
    ];

    const financialChildren = hasBalanceSheetEnabled
        ? [
              ...financials,

              {
                  key: 'financials-balance-sheet',
                  label: 'Balance Sheet',
                  showItem: featurePermissions?.includes(
                      AppFeaturePermissions.BalanceSheet
                  ),
              },
          ]
        : financials;

    const strategyAndPlanningChildren = [
        {
            key: ANNUAL_PRIORITIES_AND_OBJECTIVES_CARD_TYPE,
            label: 'Priorities & Objectives',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.PrioritiesAndObjectives
            ),
        },
        {
            key: MARKET_OVERVIEW_CARD_TYPE,
            label: 'Market Overview',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.MarketOverview
            ),
        },
        {
            key: SWOT_CARD_TYPE,
            label: 'SWOT',
            showItem: featurePermissions?.includes(AppFeaturePermissions.Swot),
        },
        {
            key: HOLD_SELL_CARD_TYPE,
            label: 'Hold/Sell',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.HoldSell
            ),
        },
        {
            key: LEASING_GUIDELINES_CARD_TYPE,
            label: 'Leasing Guidelines',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.LeasingGuidelines
            ),
        },
        {
            key: CONTRACTS_CARD_TYPE,
            label: 'Service Contracts',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.Contracts
            ),
        },
        {
            key: LEASING_PLAN_CARD_TYPE,
            label: 'Leasing Plan',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.LeasingPlan
            ),
        },
    ];

    const valuationsChildren = [
        {
            key: VALUATIONS_CARD_TYPE,
            label: 'Valuations',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.Valuations
            ),
        },
    ];

    const agedReceivablesChildren = [
        {
            key: AGED_RECEIVABLES_CARD_TYPE,
            label: 'Aged Receivables',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.AgedReceivables
            ),
        },
    ];

    const commonItems = [
        {
            key: 'executive-summary',
            label: 'Executive Summary',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.ExecutiveSummary
            ),
        },
        {
            key: 'property-attributes',
            label: 'Property Attributes',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.Attributes
            ),
        },
    ];

    const overviewChildrenHasOwnership = hasOwnershipPercentageEnabled
        ? [
              {
                  key: 'ownership',
                  label: 'Ownership',
                  showItem: true,
              },
              ...commonItems,
          ]
        : commonItems;

    const overviewChildren = [
        {
            key: 'property-info',
            label: 'Property Information',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.PropertyInformation
            ),
        },
    ];

    overviewChildren.push(...overviewChildrenHasOwnership);

    const unvalidatedMenuItems: (boolean | MenuItem)[] = [
        filterShowItems(overviewChildren).some((b) => b.showItem) && {
            key: 'overview',
            label: (
                <>
                    <i
                        className="fa-solid fa-building"
                        style={{
                            padding: '2px 5px',
                            marginRight: siderCollapsed ? 0 : 7,
                        }}
                    />
                    {siderCollapsed ? '' : ' Overview'}
                </>
            ),
            children: filterShowItems(overviewChildren),
        },
        filterShowItems(leaseChildren).some((b) => b.showItem) && {
            key: 'leases',
            label: (
                <>
                    <i
                        className="fa-solid fa-list-alt"
                        style={{
                            padding: '2px 3px',
                            marginRight: siderCollapsed ? 0 : 7,
                        }}
                    />
                    {siderCollapsed ? '' : ' Leasing'}
                </>
            ),
            children: filterShowItems(leaseChildren),
        },
        filterShowItems(financialChildren).some((b) => b.showItem) && {
            key: 'financials',
            label: (
                <>
                    <i
                        className="fa-solid fa-usd"
                        style={{
                            padding: '2px 5px',
                            marginRight: siderCollapsed ? 0 : 7,
                        }}
                    />
                    {siderCollapsed ? '' : ' Financials'}
                </>
            ),
            children: filterShowItems(financialChildren),
        },
    ];

    const sectionMenuItems: MenuItem[] = unvalidatedMenuItems.filter(
        Boolean
    ) as MenuItem[];

    const strategyAndPlanningSection = filterShowItems(
        strategyAndPlanningChildren
    ).some((c) => c.showItem) && {
        key: 'strategyAndPlanning',
        label: (
            <>
                <i
                    className="fa-solid fa-tasks"
                    style={{
                        padding: '2px 5px',
                        marginRight: siderCollapsed ? 0 : 7,
                    }}
                />
                {siderCollapsed ? '' : ' Strategy & Planning'}
            </>
        ),
        children: filterShowItems(strategyAndPlanningChildren),
    };

    const capitalProjectsChildren = filterShowItems([
        {
            key: 'capital-projects',
            label: 'Projects / Plan',
            showItem: featurePermissions?.includes(
                AppFeaturePermissions.CapitalProjects
            ),
        },
    ]);
    const capitalProjectsSectionMenuItem = filterShowItems(
        capitalProjectsChildren
    ).some((c) => c.showItem) && {
        key: 'projects',
        label: (
            <>
                <i
                    className="fa-solid fa-gavel"
                    style={{
                        padding: '2px 5px',
                        marginRight: siderCollapsed ? 0 : 7,
                    }}
                />
                {siderCollapsed ? '' : ' Capital'}
            </>
        ),
        children: capitalProjectsChildren,
    };

    const valuationsSection = filterShowItems(valuationsChildren).some(
        (c) => c.showItem
    ) && {
        key: 'valuations',
        label: (
            <>
                <i
                    className="fa-solid fa-money-bill-trend-up"
                    style={{
                        padding: '2px 5px',
                        marginRight: siderCollapsed ? 0 : 7,
                    }}
                />
                {siderCollapsed ? '' : ' Valuations'}
            </>
        ),
        children: filterShowItems(valuationsChildren),
    };

    const agedReceivablesSection = filterShowItems(
        agedReceivablesChildren
    ).some((c) => c.showItem) && {
        key: 'agedReceivables',
        label: (
            <>
                <i
                    className="fa-solid fa-calendar"
                    style={{
                        padding: '2px 5px',
                        marginRight: siderCollapsed ? 0 : 7,
                    }}
                />
                {siderCollapsed ? '' : ' Aged Receivables'}
            </>
        ),
        children: filterShowItems(agedReceivablesChildren),
    };

    if (hasAgedReceivablesEnabled && agedReceivablesSection) {
        sectionMenuItems.push(agedReceivablesSection);
    }

    if (hasPlanningSectionEnabled && strategyAndPlanningSection) {
        sectionMenuItems.splice(1, 0, strategyAndPlanningSection);
    }

    if (hasCapitalTrackerEnabled && capitalProjectsSectionMenuItem) {
        sectionMenuItems.push(capitalProjectsSectionMenuItem);
    }

    if (valuationsSection) {
        sectionMenuItems.push(valuationsSection);
    }

    const [activeSection, setActiveSection] = useState(
        sectionQueryParam || sectionMenuItems[0]?.key
    );

    const [menuSelection, setMenuSelection] = useState(
        subsectionQueryParam || sectionMenuItems?.[0]?.children?.[0]?.key
    );

    useEffect(() => {
        if (sectionMenuItems.length > 0) {
            const sectionExists = sectionMenuItems.some(
                (item) => item.key === sectionQueryParam
            );
            const subsectionExists = sectionMenuItems.some((item) =>
                item.children?.some(
                    (child) => child.key === subsectionQueryParam
                )
            );

            if (!sectionExists) {
                clearQueryParam(SECTION_KEY);
                clearQueryParam(SUBSECTION_KEY);
                setActiveSection(sectionMenuItems[0]?.key);
                setMenuSelection(sectionMenuItems[0]?.children?.[0]?.key);
            }

            if (sectionExists && subsectionExists) {
                setActiveSection(sectionQueryParam || sectionMenuItems[0]?.key);
                setMenuSelection(
                    subsectionQueryParam ||
                        sectionMenuItems[0]?.children?.[0]?.key
                );
            }
        }
    }, [sectionMenuItems, sectionQueryParam, subsectionQueryParam]);

    useEffect(() => {
        const element = document.getElementById(menuSelection || '');

        if (element) {
            element.scrollIntoView({ block: 'start' });
        }
    }, [menuSelection]);

    const onClick: MenuProps['onClick'] = (e) => {
        const newMenuSelection = e.key;
        clearQueryParam('comment_id');
        setMenuSelection(newMenuSelection);
        setQueryParam(SUBSECTION_KEY, newMenuSelection);

        if (e.keyPath.length > 1) {
            const newSection = e.keyPath[e.keyPath.length - 1];

            setActiveSection(newSection);
            setQueryParam(SECTION_KEY, newSection);
        }
    };

    const sectionsByRootMenuItemKey: Dictionary<() => ReactElement> = {
        overview: () => <PropertyDetailSection entityCode={entityCode} />,
        strategyAndPlanning: () => (
            <StrategyPlanningSection entityCode={entityCode} />
        ),
        leases: () => (
            <LeasesSection
                hasOccupancyTrendEnabled={hasOccupancyTrendEnabled}
                hasUnitMixEnabled={hasUnitMixEnabled}
                entityCode={entityCode}
            />
        ),
        financials: () => (
            <FinancialSection
                hasBalanceSheetEnabled={hasBalanceSheetEnabled}
                entityCodes={[entityCode]}
            />
        ),
        projects: () => <CapitalProjectsSection entityCode={entityCode} />,
        valuations: () => <ValuationsSection entityCode={entityCode} />,
        agedReceivables: () => (
            <AgedReceivablesSection entityCode={entityCode} />
        ),
    };

    const SectionComponent = useMemo(
        () => sectionsByRootMenuItemKey[activeSection] ?? (() => null),
        [activeSection]
    );

    if (
        !entityCodes.filteredEntityCodes.includes(entityCode) ||
        !sectionMenuItems?.length
    ) {
        return (
            <div style={{ width: '100%' }}>
                <DisabledDashboard text={'No data'} />
            </div>
        );
    }

    return (
        <Layout style={{ height: '100%', flexWrap: 'wrap' }}>
            <Header className={headerStyle}>
                {propertyInfo ? (
                    <Typography.Title
                        level={3}
                        className={propertyNameStyle}
                        data-test="property-detail-property-title"
                    >
                        {propertyInfo.name} ({propertyInfo.display_code})
                    </Typography.Title>
                ) : (
                    <Skeleton paragraph={{ rows: 0 }} active />
                )}

                <div
                    className={backToMap}
                    data-test="property-detail-back-to-map-container"
                >
                    <BackToMap />
                </div>

                <div style={{ display: 'flex', marginTop: '8px' }}>
                    {hasOwnershipPercentageEnabled ? (
                        <div className={dataLevelWrapper}>
                            <EntityOwnershipDataLevelDropdown />
                        </div>
                    ) : null}

                    <div className={asOfDateContainer}>
                        <AsOfDateIcon entityCode={entityCode} />
                    </div>
                </div>
            </Header>

            {/* left sidebar */}
            <Sider
                style={{
                    height: '100vh',
                    overflowY: 'auto',
                }}
                width={220}
                theme={'dark'}
                collapsible
                collapsed={siderCollapsed}
                onCollapse={(collapsed) => setSiderCollapsed(collapsed)}
            >
                <PropertyProfileMenu
                    defaultOpenKeys={[activeSection]}
                    menuItems={sectionMenuItems}
                    onClick={onClick}
                    selectedKeys={[menuSelection || '']}
                />
                <div style={{ height: '75px' }}>
                    {/* placeholder div to prevent vertical cutoff and enable scrolling to bottom */}
                </div>
            </Sider>

            {/* main content */}
            <Content className={mainContentPane}>
                <SWRConfig
                    value={{
                        revalidateOnFocus: false,
                        revalidateOnMount: true,
                        dedupingInterval: 5000,
                    }}
                >
                    <SectionComponent key={activeSection} />
                </SWRConfig>
            </Content>
        </Layout>
    );
};

const mapState = (s: RootStateOrAny): PropertyDetailContainerProps => {
    const entityCode = getPureId(s);
    const properties = selectPropertiesById(s, { propertyIds: [entityCode] });

    return {
        entityCode: getPureId(s) ?? '',
        propertyInfo: properties[Object.keys(properties)[0]],
        hasOccupancyTrendEnabled: hasOccupancyTrendEnabled(s),
        hasBalanceSheetEnabled: hasBalanceSheetEnabled(s),
        hasCapitalTrackerEnabled: hasCapitalTrackerEnabled(s),
        hasPlanningSectionEnabled: hasPlanningSectionEnabled(s),
        hasUnitMixEnabled: hasUnitMixEnabled(s),
        hasAgedReceivablesEnabled: hasAgedReceivablesEnabled(s),
        hasOwnershipPercentageEnabled: hasOwnershipPercentageEnabled(s),
    };
};

export default connect(mapState)(PropertyProfileContainer);
